// SortableSectorList.js
// Reusable sorted sector rows for DailyRoundupCard and WeeklyWrapCard.
// Renders a sort toggle button + sector name / % change rows.
// Sort cycles: default order → desc (gainers first) → asc (losers first) → default.

import React, { useState, useMemo } from 'react';

const ArrowUp   = () => <span className="arrow-up">▲</span>;
const ArrowDown = () => <span className="arrow-down">▼</span>;

const ChangeCell = ({ value, direction = 'auto' }) => {
    const v = Number(value ?? 0);
    const isUp = direction === 'up' ? true : direction === 'down' ? false : v >= 0;
    return (
        <span className={isUp ? 'chg-up' : 'chg-down'}>
            {isUp ? <ArrowUp /> : <ArrowDown />}
            {Math.abs(v).toFixed(2)}%
        </span>
    );
};

const SortableSectorList = ({ rows = [], narrative }) => {
    // 'desc' = gainers first (default), 'asc' = losers first, null = original AI order
    const [sortDir, setSortDir] = useState('desc');

    // Cycle: desc (gainers first) → asc (losers first) → null (AI order) → desc
    const cycleSort = () =>
        setSortDir(d => d === 'desc' ? 'asc' : d === 'asc' ? null : 'desc');

    const sortedRows = useMemo(() => {
        if (!sortDir) return rows;
        return [...rows].sort((a, b) => {
            const av = Number(a.wowPercent) || 0;
            const bv = Number(b.wowPercent) || 0;
            return sortDir === 'desc' ? bv - av : av - bv;
        });
    }, [rows, sortDir]);

    const sortIcon  = sortDir === null ? '↕' : sortDir === 'desc' ? '↓' : '↑';
    const sortTitle = sortDir === null
        ? 'Sort by % change (gainers first)'
        : sortDir === 'desc' ? 'Sort ascending (losers first)' : 'Reset to default order';

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.35rem' }}>
                <button
                    onClick={cycleSort}
                    title={sortTitle}
                    className={`sector-sort-btn${sortDir ? ' sector-sort-btn--active' : ''}`}
                >
                    {sortIcon} % chg
                </button>
            </div>

            {sortedRows.map((row, i) => (
                <div key={i} className="sector-row">
                    <span className="sector-name">{row.sector}</span>
                    {Number(row.wowPercent) !== 0
                        ? <ChangeCell value={row.wowPercent} direction={row.direction} />
                        : <span className={row.direction === 'up' ? 'chg-up' : 'chg-down'}>
                            {row.direction === 'up' ? <ArrowUp /> : <ArrowDown />}
                          </span>
                    }
                </div>
            ))}

            {narrative && (
                <div className="wrap-description" style={{ marginTop: '0.5rem' }}>
                    {narrative}
                </div>
            )}
        </>
    );
};

export default SortableSectorList;
